import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { api } from "@/lib/api";

function formatDateTime(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  return dt.toLocaleString("tr-TR");
}

function formatTryRight(amount) {
  return `${Number(amount || 0).toLocaleString("tr-TR")} ₺`;
}

function statusPillClass(isPending) {
  return `inline-flex rounded-xl px-2.5 py-1 text-xs font-semibold ${
    isPending ? "bg-amber-50 text-amber-700 ring-1 ring-amber-200" : "bg-emerald-50 text-emerald-700 ring-1 ring-emerald-200"
  }`;
}

export default function SubscriptionsPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [pendingRows, setPendingRows] = useState([]);
  const [planMap, setPlanMap] = useState({});
  const [me, setMe] = useState(null);
  const [trialStatus, setTrialStatus] = useState({ is_trial: false, days_left: null });
  const [billingPackages, setBillingPackages] = useState([]);
  const [integrations, setIntegrations] = useState([]);
  const [upgradeModalOpen, setUpgradeModalOpen] = useState(false);
  const [upgradeCycle, setUpgradeCycle] = useState("monthly");
  const [upgradePlan, setUpgradePlan] = useState("");
  const [upgradeSaving, setUpgradeSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteReason, setDeleteReason] = useState("");
  const [deleteNote, setDeleteNote] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);
  const [cancelModalOpen, setCancelModalOpen] = useState(false);
  const [cancelReason, setCancelReason] = useState("");
  const [cancelNote, setCancelNote] = useState("");
  const [cancelSubmitting, setCancelSubmitting] = useState(false);

  const activeSubscription = useMemo(() => {
    if (!me) return null;
    return {
      plan: String(me.subscription_plan || "trial"),
      status: String(me.subscription_status || "active"),
      renewAt: me.subscription_expires_at || null,
    };
  }, [me]);

  const loadData = async () => {
    setLoading(true);
    try {
      const [pendingRes, packagesRes, meRes, trialRes, integrationsRes] = await Promise.all([
        api.get("/billing/checkout-intents", { params: { status: "pending_payment" } }),
        api.get("/billing/packages").catch(() => null),
        api.get("/auth/me").catch(() => null),
        api.get("/billing/trial-status").catch(() => null),
        api.get("/integrations").catch(() => []),
      ]);
      setPendingRows(Array.isArray(pendingRes?.items) ? pendingRes.items : []);
      setMe(meRes || null);
      const plans = {};
      for (const p of (packagesRes?.packages || [])) plans[String(p.key || "").toLowerCase()] = p;
      setPlanMap(plans);
      setBillingPackages(Array.isArray(packagesRes?.packages) ? packagesRes.packages : []);
      setIntegrations(Array.isArray(integrationsRes) ? integrationsRes : []);
      setTrialStatus({
        is_trial: Boolean(trialRes?.is_trial),
        days_left: typeof trialRes?.days_left === "number" ? trialRes.days_left : null,
      });
    } catch (err) {
      toast.error(err?.message || "Abonelik verileri yüklenemedi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const openPayment = (intentId) => {
    navigate(`/panel/hesabim/faturalandirma?view=payment&intent=${encodeURIComponent(intentId)}`);
  };

  const activePlanKey = String(activeSubscription?.plan || "").toLowerCase();
  const activeIntegration = useMemo(
    () => (integrations || []).find((x) => String(x?.kind || "").toLowerCase() !== "cargo") || null,
    [integrations]
  );
  const activePlanPackage = billingPackages.find((p) => String(p.key || "").toLowerCase() === activePlanKey);
  const activeCycle = "monthly";
  const activeFee = activePlanPackage ? Number(activeCycle === "yearly" ? activePlanPackage.yearly_price : activePlanPackage.monthly_price) : 0;
  const trialPaymentUrgent = Boolean(trialStatus?.is_trial && Number(trialStatus?.days_left ?? 99) <= 7);
  const canDeleteActiveSubscription = activePlanKey === "trial";

  const openUpgradeModal = () => {
    const options = billingPackages.filter((p) => {
      const key = String(p.key || "").toLowerCase();
      if (activePlanKey === "starter") return key === "pro" || key === "growth" || key === "enterprise";
      if (activePlanKey === "pro" || activePlanKey === "growth") return key === "enterprise";
      if (activePlanKey === "trial") return key !== "trial";
      return false;
    });
    if (options.length === 0) {
      toast.error("Yükseltilebilir paket bulunamadı.");
      return;
    }
    setUpgradePlan(String(options[0].key || ""));
    setUpgradeCycle("monthly");
    setUpgradeModalOpen(true);
  };

  const createUpgradeIntent = async () => {
    if (!upgradePlan) return;
    setUpgradeSaving(true);
    try {
      const res = await api.post("/billing/subscription/upgrade", {
        plan_key: upgradePlan,
        billing_cycle: upgradeCycle,
      });
      setUpgradeModalOpen(false);
      toast.success("Abonelik planı güncellendi");
      await loadData();
    } catch (err) {
      toast.error(err?.message || "Yükseltme kaydı oluşturulamadı");
    } finally {
      setUpgradeSaving(false);
    }
  };

  const createTrialPaymentIntent = async () => {
    try {
      const fallbackPlan = billingPackages.find((p) => String(p.key || "").toLowerCase() !== "trial");
      const planKey = activePlanKey && activePlanKey !== "trial" ? activePlanKey : String(fallbackPlan?.key || "");
      if (!planKey) {
        toast.error("Ödeme planı bulunamadı.");
        return;
      }
      const res = await api.post("/billing/checkout-intent", {
        plan_key: planKey,
        billing_cycle: "monthly",
        source: "trial_expiry",
        provider: integrations[0]?.provider || "",
        integration_kind: integrations[0]?.kind || "store",
        redirect_to: "/panel/hesabim/abonelik",
      });
      if (res?.checkout_intent_id) {
        navigate(`/panel/hesabim/faturalandirma?view=payment&intent=${encodeURIComponent(res.checkout_intent_id)}`);
      } else {
        navigate("/panel/hesabim/faturalandirma?view=payment");
      }
    } catch (err) {
      toast.error(err?.message || "Ödeme kaydı oluşturulamadı");
    }
  };

  const openDeletePending = (row) => {
    setDeleteTarget(row);
    setDeleteReason("");
    setDeleteNote("");
  };

  const submitDeletePending = async () => {
    if (!deleteTarget?.id) return;
    setDeleteSubmitting(true);
    try {
      await api.delete(`/billing/checkout-intents/${deleteTarget.id}`, {
        data: { reason: deleteReason, note: deleteNote },
      });
      setPendingRows((prev) => prev.filter((x) => x.id !== deleteTarget.id));
      toast.success("Tamamlanmamış abonelik kaydı silindi");
      setDeleteTarget(null);
    } catch (err) {
      toast.error(err?.message || "Kayıt silinemedi");
    } finally {
      setDeleteSubmitting(false);
    }
  };

  const submitCancelSubscription = async () => {
    if (!canDeleteActiveSubscription) {
      toast.error("Sadece deneme abonelikleri silinebilir.");
      return;
    }
    setCancelSubmitting(true);
    try {
      await api.post("/billing/subscription/cancel", {
        integration_id: activeIntegration?.id || null,
        reason: cancelReason || null,
        note: cancelNote || null,
      });
      toast.success("Abonelik ve bağlı entegrasyon silindi");
      setCancelModalOpen(false);
      await loadData();
    } catch (err) {
      toast.error(err?.message || "Abonelik silinemedi");
    } finally {
      setCancelSubmitting(false);
    }
  };

  if (loading) {
    return <div className="p-6 text-sm text-muted-foreground">Abonelik verileri yükleniyor...</div>;
  }

  return (
    <div className="page-shell space-y-5">
      <div className="page-header mb-2">
        <div className="app-page-header">
          <h1 className="app-page-title">Abonelik</h1>
          <p className="app-page-subtitle">Abonelik planınızı ve fatura detaylarınızı yönetin.</p>
        </div>
      </div>

        <section className="surface-card-soft rounded-2xl p-4">
          <div className="text-sm font-semibold text-foreground">Abonelik Detayı</div>
          <div className="mt-2 grid gap-3 md:grid-cols-3">
            <div className="rounded-xl border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Plan</div>
              <div className="mt-1 text-sm font-semibold text-foreground">{activeSubscription?.plan || "-"}</div>
            </div>
            <div className="rounded-xl border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Durum</div>
              <div className="mt-1">
                <span className={statusPillClass(activeSubscription?.status !== "active")}>
                  {activeSubscription?.status === "active" ? "Aktif" : (activeSubscription?.status || "-")}
                </span>
              </div>
            </div>
            <div className="rounded-xl border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Yenileme Tarihi</div>
              <div className="mt-1 text-sm font-semibold text-foreground">{formatDateTime(activeSubscription?.renewAt)}</div>
            </div>
            <div className="rounded-xl border border-border bg-muted/30 p-3">
              <div className="text-xs text-muted-foreground">Abonelik Ücreti</div>
              <div className="mt-1 text-sm font-semibold text-foreground">{formatTryRight(activeFee)} / Ay</div>
            </div>
          </div>
          <div className="mt-3 flex flex-wrap items-center gap-2">
            {trialPaymentUrgent ? (
              <button
                type="button"
                onClick={createTrialPaymentIntent}
                className="h-9 rounded-xl bg-[#1487a3] hover:bg-[#12779b] px-4 text-xs font-semibold text-white hover:opacity-95"
              >
                Ödemeye Geç
              </button>
            ) : (activePlanKey === "starter" || activePlanKey === "pro" || activePlanKey === "growth" || activePlanKey === "trial") ? (
              <button
                type="button"
                onClick={openUpgradeModal}
                className="h-9 rounded-xl bg-[#1487a3] hover:bg-[#12779b] px-4 text-xs font-semibold text-white hover:opacity-95"
              >
                Aboneliği Yükselt
              </button>
            ) : null}
            {trialPaymentUrgent ? (
              <span className="text-xs font-semibold text-amber-700">
                {Number(trialStatus?.days_left) <= 1 ? "1 gün kaldı" : `${trialStatus?.days_left} gün kaldı`}
              </span>
            ) : null}
          </div>
        </section>

        <section className="surface-card-soft overflow-hidden">
          <div className="px-4 py-3 text-sm font-semibold text-foreground">Aktif Abonelikler</div>
          <div className="p-3">
            <div className="rounded-xl border border-border bg-muted/20 px-4 py-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1.2fr_1fr_1fr_1fr] md:items-center">
                <div>
                  <div className="text-sm font-semibold text-foreground">{activePlanPackage?.name || activeSubscription?.plan || "-"}</div>
                  <div className="mt-1 text-xs text-muted-foreground">Entegrasyon: {activeIntegration?.name || "Entegrasyon yok"}</div>
                </div>
                <div className="text-xs text-muted-foreground">
                  <div>Durum</div>
                  <div className="mt-1"><span className={statusPillClass(activeSubscription?.status !== "active")}>{activeSubscription?.status === "active" ? "Aktif" : (activeSubscription?.status || "-")}</span></div>
                </div>
                <div>
                  <div className="text-[11px] text-muted-foreground">Abonelik Ücreti</div>
                  <div className="mt-1 text-sm font-semibold text-foreground">{formatTryRight(activeFee)} / Ay</div>
                </div>
                <div>
                  <div className="text-[11px] text-muted-foreground">Yenileme</div>
                  <div className="mt-1 text-sm font-semibold text-foreground">{formatDateTime(activeSubscription?.renewAt)}</div>
                </div>
              </div>
              <div className="mt-3 flex justify-end gap-2">
                {(activePlanKey === "starter" || activePlanKey === "pro" || activePlanKey === "growth" || activePlanKey === "trial") ? (
                  <button
                    type="button"
                    onClick={openUpgradeModal}
                    className="h-9 rounded-xl bg-[#1487a3] hover:bg-[#12779b] px-4 text-xs font-semibold text-white hover:opacity-95"
                  >
                    Aboneliği Yükselt
                  </button>
                ) : null}
                {canDeleteActiveSubscription ? (
                  <button
                    type="button"
                    onClick={() => {
                      setCancelReason("");
                      setCancelNote("");
                      setCancelModalOpen(true);
                    }}
                    className="h-9 rounded-xl border border-rose-200 bg-rose-50 px-4 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                  >
                    Sil
                  </button>
                ) : null}
              </div>
            </div>
          </div>
        </section>

        <section className="surface-card-soft overflow-hidden">
          <div className="px-4 py-3 text-sm font-semibold text-foreground">Tamamlanmayı Bekleyenler</div>
          <div className="space-y-3 p-3">
            {pendingRows.length === 0 ? (
              <div className="px-3 py-8 text-sm text-muted-foreground">Bekleyen abonelik kaydı yok.</div>
            ) : pendingRows.map((row) => {
              const plan = planMap[String(row.plan_key || "").toLowerCase()];
              return (
                <div key={row.id} className="rounded-xl border border-border bg-muted/20 px-4 py-4">
                  <div className="grid grid-cols-1 gap-3 md:grid-cols-[1.2fr_0.9fr_0.9fr] md:items-center">
                    <div>
                      <div className="text-sm font-semibold text-foreground">{plan?.name || row.plan_key || "Paket"}</div>
                      <div className="mt-1 text-xs text-muted-foreground">Kayıt No: {row.id}</div>
                    </div>
                    <div>
                      <span className={statusPillClass(true)}>Ödeme Bekleniyor</span>
                      <div className="mt-2 text-xs text-muted-foreground">{formatDateTime(row.updated_at || row.created_at)}</div>
                    </div>
                    <div className="text-left md:text-right">
                      <div className="text-[11px] text-muted-foreground">Tutar</div>
                      <div className="mt-1 text-base font-semibold text-foreground">{formatTryRight(row.amount || 0)}</div>
                    </div>
                  </div>
                  <div className="mt-3 flex justify-end gap-2">
                    <button
                      type="button"
                      onClick={() => openDeletePending(row)}
                      className="h-9 rounded-xl border border-rose-200 bg-rose-50 px-4 text-xs font-semibold text-rose-700 hover:bg-rose-100"
                    >
                      Sil
                    </button>
                    {trialPaymentUrgent ? (
                      <button
                        type="button"
                        onClick={() => openPayment(row.id)}
                        className="h-9 rounded-xl bg-[#1487a3] hover:bg-[#12779b] px-4 text-xs font-semibold text-white hover:opacity-95"
                      >
                        Ödemeye Geç
                      </button>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
        {deleteTarget ? (
          <section className="rounded-2xl border border-rose-200 bg-card p-4 shadow-[0_10px_35px_rgba(120,27,27,0.08)]">
            <div className="text-sm font-semibold text-rose-700">Tamamlanmamış abonelik kaydını sil</div>
            <p className="mt-1 text-xs text-muted-foreground">Sadece bekleyen kayıtlar silinebilir. Tamamlanan kayıtlar korunur.</p>
            <div className="mt-3 space-y-2">
              <label className="block text-xs font-medium text-muted-foreground">Neden siliyorsun?</label>
              <select
                value={deleteReason}
                onChange={(e) => setDeleteReason(e.target.value)}
                className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm"
              >
                <option value="">Seçiniz</option>
                <option value="wrong_plan">Yanlış paket seçtim</option>
                <option value="duplicate">Mükerrer kayıt</option>
                <option value="postpone">Sonra ödeyeceğim</option>
                <option value="other">Diğer</option>
              </select>
              <textarea
                value={deleteNote}
                onChange={(e) => setDeleteNote(e.target.value)}
                placeholder="Kısa not (opsiyonel)"
                className="min-h-[84px] w-full rounded-xl border border-border bg-card px-3 py-2 text-sm"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setDeleteTarget(null)}
                  className="h-9 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted/30"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={submitDeletePending}
                  disabled={deleteSubmitting}
                  className="h-9 rounded-xl border border-rose-200 bg-rose-50 px-3 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {deleteSubmitting ? "Siliniyor..." : "Kaydı Sil"}
                </button>
              </div>
            </div>
          </section>
        ) : null}
        {upgradeModalOpen ? (
          <section className="surface-card-soft p-4">
            <div className="text-sm font-semibold text-foreground">Paket Seçiniz</div>
            <div className="mt-3 inline-flex rounded-full border border-border bg-muted p-1">
              <button
                type="button"
                onClick={() => setUpgradeCycle("yearly")}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold ${upgradeCycle === "yearly" ? "bg-[#1487a3] text-white" : "text-muted-foreground"}`}
              >
                Yıllık
              </button>
              <button
                type="button"
                onClick={() => setUpgradeCycle("monthly")}
                className={`rounded-full px-4 py-1.5 text-xs font-semibold ${upgradeCycle === "monthly" ? "bg-[#1487a3] text-white" : "text-muted-foreground"}`}
              >
                Aylık
              </button>
            </div>
            <div className="mt-3 space-y-2">
              {billingPackages
                .filter((p) => {
                  const key = String(p.key || "").toLowerCase();
                  if (activePlanKey === "starter") return key === "pro" || key === "growth" || key === "enterprise";
                  if (activePlanKey === "pro" || activePlanKey === "growth") return key === "enterprise";
                  if (activePlanKey === "trial") return key !== "trial";
                  return false;
                })
                .map((p) => {
                  const amount = Number(upgradeCycle === "yearly" ? p.yearly_price : p.monthly_price);
                  const selected = upgradePlan === p.key;
                  return (
                    <button
                      key={p.key}
                      type="button"
                      onClick={() => setUpgradePlan(String(p.key || ""))}
                      className={`w-full rounded-lg border p-3 text-left ${selected ? "border-[#1487a3] bg-sky-50" : "border-border bg-card hover:bg-muted/40"}`}
                    >
                      <div className="flex items-center justify-between gap-3">
                        <div className="text-sm font-semibold">{String(p.name || "").replace("growth", "Pro").replace("Growth", "Pro")}</div>
                        <div className="text-sm font-semibold">{amount.toLocaleString("tr-TR")} ₺ / {upgradeCycle === "yearly" ? "Yıl" : "Ay"}</div>
                      </div>
                    </button>
                  );
                })}
            </div>
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setUpgradeModalOpen(false)}
                className="h-9 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted/30"
              >
                Vazgeç
              </button>
              <button
                type="button"
                onClick={createUpgradeIntent}
                disabled={upgradeSaving}
                className="h-9 rounded-xl bg-[#1487a3] hover:bg-[#12779b] px-4 text-xs font-semibold text-white hover:opacity-95 disabled:opacity-60"
              >
                {upgradeSaving ? "Hazırlanıyor..." : "Aboneliği Yükselt"}
              </button>
            </div>
          </section>
        ) : null}
        {cancelModalOpen ? (
          <section className="rounded-2xl border border-rose-200 bg-card p-4 shadow-[0_10px_35px_rgba(120,27,27,0.08)]">
            <div className="text-sm font-semibold text-rose-700">Aboneliği sil</div>
            <p className="mt-1 text-xs text-muted-foreground">Bu işlem mevcut aboneliği ve bağlı entegrasyonu siler.</p>
            <div className="mt-3 space-y-2">
              <label className="block text-xs font-medium text-muted-foreground">Neden siliyorsun?</label>
              <select
                value={cancelReason}
                onChange={(e) => setCancelReason(e.target.value)}
                className="h-10 w-full rounded-xl border border-border bg-card px-3 text-sm"
              >
                <option value="">Seçiniz</option>
                <option value="not_needed">Artık ihtiyacım yok</option>
                <option value="switch_provider">Farklı entegrasyona geçiyorum</option>
                <option value="price">Fiyat uygun değil</option>
                <option value="other">Diğer</option>
              </select>
              <textarea
                value={cancelNote}
                onChange={(e) => setCancelNote(e.target.value)}
                placeholder="Kısa not (opsiyonel)"
                className="min-h-[84px] w-full rounded-xl border border-border bg-card px-3 py-2 text-sm"
              />
              <div className="flex justify-end gap-2">
                <button
                  type="button"
                  onClick={() => setCancelModalOpen(false)}
                  className="h-9 rounded-xl border border-border bg-card px-3 text-xs font-semibold text-foreground hover:bg-muted/30"
                >
                  Vazgeç
                </button>
                <button
                  type="button"
                  onClick={submitCancelSubscription}
                  disabled={cancelSubmitting}
                  className="h-9 rounded-xl border border-rose-200 bg-rose-50 px-3 text-xs font-semibold text-rose-700 hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-60"
                >
                  {cancelSubmitting ? "Siliniyor..." : "Aboneliği Sil"}
                </button>
              </div>
            </div>
          </section>
        ) : null}
    </div>
  );
}
